import {
	IonButton,
	IonButtons,
	IonCheckbox,
	IonContent,
	IonHeader,
	IonIcon,
	IonInput,
	IonItem,
	IonLabel,
	IonList,
	IonListHeader,
	IonModal,
	IonPage,
	IonRange,
	IonSegment,
	IonSegmentButton,
	IonTitle,
	IonToggle,
	IonToolbar,
	RangeChangeEventDetail,
	RangeCustomEvent,
} from '@ionic/react';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SectionTitle } from '../components/Global/styles';
import { Default_LatLng } from '../application/config';
import { useAppDispatch, useAppSelector } from '../state/hooks';
import { RootState } from '../state/store';
import { batch, shallowEqual } from 'react-redux';
import {
	UPDATE_PREFS_ACCESSIBILITY,
	UPDATE_PREFS_AVATAR,
	UPDATE_PREFS_MAX_DIFFICULTY,
	UPDATE_PREFS_MIN_DIFFICULTY,
	UPDATE_PREFS_NAME,
	UPDATE_PREFS_UNITS,
	UPDATE_PREFS_UNITS_TEMP,
} from '../state/prefsSlice';
import {
	ChecklistItem,
	PermissionActions,
	PermissionCard,
	PermissionInfo,
	PermissionList,
	PermissionStatusBadge,
	ProfileAvatar,
	ProfileAvatarImg,
	ProfileAvatarStack,
	ProfileSection,
	ProfileSectionTitle,
} from '../components/Global/profileStyles';
import { ITaxonomy } from '../models/interfaces/ITaxonomy';
import { ActivityList } from '../models/factories/ActivityFactory';
import { TopicList } from '../models/factories/TopicFactory';
import Activities from '../components/Catalogue/Activities/Activities';
import Footer from '../components/Global/Footer/Footer';
import { CloseButton } from '../components/TrailPage/VirtualTour/virtualTourStyles';
import { arrowBackCircle, close } from 'ionicons/icons';
import {
	IonToggleCustomEvent,
	ToggleChangeEventDetail,
	IonSegmentCustomEvent,
} from '@ionic/core';
// import { AVATAR_UPDATE, avatarOptions } from '../state/avatarSlice';
import { avatarOptions } from '../state/prefsSlice';
import Range from '../components/Profile/Range';
import ActivitiesList from '../components/Profile/ActivitiesList';
import { Backbutton } from '../components/Global/InnerPageComponents/InnerPageStyles';
import { useHistory } from 'react-router';
import PageHeader from '../components/Global/PageHeader/PageHeader';
import TopicsList from '../components/Profile/TopicsList';
import { TOGGLE_NETWORK } from '../state/networkSlice';
import { selectPrefs } from '../state/selectors';
import { db } from '../models/DBUtils';
import { persistor } from '../state/store';
import { UNSET_LOCATION, UPDATE_LOCATION } from '../state/locationSlice';

type ProfileProps = {
	activities: ITaxonomy[];
	topics: ITaxonomy[];
};

type PermissionStatusItem = {
	id: string;
	label: string;
	status: string;
	detail: string;
};

type BrowserLocationError = Error & {
	code?: number;
};

const Profile: React.FC<ProfileProps> = ({ activities, topics }) => {
	const [canDismiss, setCanDismiss] = useState(false);
	const [presentingElement, setPresentingElement] = useState<
		HTMLElement | undefined
	>(undefined);
	const [isOpen, setIsOpen] = useState(false);
	const [isResettingApp, setIsResettingApp] = useState(false);
	const [activePermissionAction, setActivePermissionAction] = useState<
		string | null
	>(null);
	const [permissionStatuses, setPermissionStatuses] = useState<
		PermissionStatusItem[]
	>([]);

	const dispatch = useAppDispatch();
	const user_prefs = useAppSelector(selectPrefs, shallowEqual);
	const currentLat = useAppSelector(
		(state: RootState) => state.location.latitude,
	);
	const currentLng = useAppSelector(
		(state: RootState) => state.location.longitude,
	);
	const currentAccuracy = useAppSelector(
		(state: RootState) => state.location.accuracy,
	);
	const hasWorkingLocation =
		currentAccuracy > 0 ||
		currentLat !== Default_LatLng.lat ||
		currentLng !== Default_LatLng.lng;

	const avatar = useAppSelector(
		(state: RootState) => state.avatar,
		shallowEqual,
	);

	const modal = useRef<HTMLIonModalElement>(null);
	const page = useRef(undefined);

	const history = useHistory();
	const showDevTools = process.env.REACT_APP_SHOW_DEV_TOOLS === 'true';

	const handleChangeAvatar = (e: React.MouseEvent) => {
		const target = e.target as HTMLImageElement;
		dispatch({
			type: UPDATE_PREFS_AVATAR,
			payload: {
				avatar: target.src,
			},
		});
	};

	const handleChangeAccessibility = (
		e: IonToggleCustomEvent<ToggleChangeEventDetail<any>>,
	) => {
		e.stopImmediatePropagation();
		dispatch({
			type: UPDATE_PREFS_ACCESSIBILITY,
			payload: {
				accessible: e.detail.checked,
			},
		});
	};

	const handleChangeName = (e: Event) => {
		const target = e.target as HTMLIonInputElement;
		dispatch({
			type: UPDATE_PREFS_NAME,
			payload: {
				name: target.value,
			},
		});
	};
	const handleChangeDifficulty = (e: CustomEvent) => {
		batch(() => {
			dispatch({
				type: UPDATE_PREFS_MIN_DIFFICULTY,
				payload: {
					min_difficulty: e.detail.value.lower,
				},
			});
			dispatch({
				type: UPDATE_PREFS_MAX_DIFFICULTY,
				payload: {
					max_difficulty: e.detail.value.upper,
				},
			});
		});
	};

	const handlePreferredUnits = (e: any) => {
		console.log(e.detail.value);
		dispatch({
			type: UPDATE_PREFS_UNITS,
			payload: {
				preferred_dist_units: e.detail.value,
			},
		});
	};

	const handlePreferredTempUnits = (e: any) => {
		console.log(e.detail.value);
		dispatch({
			type: UPDATE_PREFS_UNITS_TEMP,
			payload: {
				preferred_temp_units: e.detail.value,
			},
		});
	};

	useEffect(() => {
		setPresentingElement(page.current);
	}, []);

	const requestBrowserLocation = useCallback(async () => {
		if (typeof window !== 'undefined' && !window.isSecureContext) {
			const error = new Error(
				'Location access requires a secure origin. Use HTTPS or localhost.',
			) as BrowserLocationError;
			error.code = 0;
			throw error;
		}

		if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
			throw new Error('Geolocation not supported in this browser');
		}

		return new Promise<void>((resolve, reject) => {
			navigator.geolocation.getCurrentPosition(
				({ coords }) => {
					dispatch({
						type: UPDATE_LOCATION,
						payload: {
							latitude: coords.latitude,
							longitude: coords.longitude,
							accuracy: coords.accuracy ?? 0,
						},
					});
					resolve();
				},
				(error) => {
					const browserError = new Error(
						error.message || 'Unable to retrieve browser location.',
					) as BrowserLocationError;
					browserError.code = error.code;
					reject(browserError);
				},
				{ enableHighAccuracy: true },
			);
		});
	}, [dispatch]);

	const requestLocationAccess = useCallback(async () => {
		await requestBrowserLocation();
	}, [requestBrowserLocation]);

	const toReadableStatus = useCallback((status: string) => {
		switch (status) {
			case 'granted':
				return {
					status: 'Allowed',
					detail: 'Access has been granted.',
				};
			case 'prompt':
			case 'prompt-with-rationale':
				return {
					status: 'Not decided',
					detail: 'The app can ask for permission when needed.',
				};
			case 'denied':
				return {
					status: 'Denied',
					detail: 'Access was denied for this app.',
				};
			default:
				return {
					status: status || 'Unknown',
					detail: 'Permission status could not be determined.',
				};
		}
	}, []);

	const loadPermissionStatuses = useCallback(async () => {
		const nextStatuses: PermissionStatusItem[] = [];

		if (hasWorkingLocation) {
			nextStatuses.push({
				id: 'geolocation',
				label: 'Location',
				status: 'Allowed',
				detail: 'Location access is working in this browser.',
			});
			setPermissionStatuses(nextStatuses);
			return;
		}

		if (
			typeof navigator !== 'undefined' &&
			'permissions' in navigator &&
			navigator.permissions?.query
		) {
			try {
				const permissionStatus = await navigator.permissions.query({
					name: 'geolocation',
				});
				const readable = toReadableStatus(permissionStatus.state);
				nextStatuses.push({
					id: 'geolocation',
					label: 'Location',
					status: readable.status,
					detail: `${readable.detail} Reported by the browser permissions API.`,
				});
			} catch (browserError) {
				nextStatuses.push({
					id: 'geolocation',
					label: 'Location',
					status: 'Unknown',
					detail: 'The permission status could not be read on this browser.',
				});
			}
		} else {
			nextStatuses.push({
				id: 'geolocation',
				label: 'Location',
				status: 'Unknown',
				detail: 'This browser does not expose geolocation permission status.',
			});
		}

		setPermissionStatuses(nextStatuses);
	}, [hasWorkingLocation, toReadableStatus]);

	useEffect(() => {
		let isMounted = true;

		const loadIfMounted = async () => {
			await loadPermissionStatuses();
		};

		if (isMounted) {
			loadIfMounted();
		}

		return () => {
			isMounted = false;
		};
	}, [loadPermissionStatuses]);

	const handleRequestPermission = async (permissionId: string) => {
		if (permissionId !== 'geolocation') {
			return;
		}

		setActivePermissionAction(permissionId);

		try {
			await requestLocationAccess();
		} catch (error) {
			const browserError = error as BrowserLocationError;
			switch (browserError.code) {
				case 1:
					window.alert(
						'Browser location access is denied for this site. In iOS Simulator, reset the site permission in Safari settings if you want to see the prompt again.',
					);
					break;
				case 2:
					window.alert(
						'The browser could not get a location from the simulator. In iOS Simulator, set a mock location under Features > Location and try again.',
					);
					break;
				case 3:
					window.alert(
						'Location request timed out before the browser returned a position. Try again after the simulator location is available.',
					);
					break;
				default:
					window.alert(
						browserError.message ||
							'Browser location could not be requested.',
					);
			}
		} finally {
			setActivePermissionAction(null);
		}

		await loadPermissionStatuses();
	};

	const handleRevokePermission = async (permissionId: string) => {
		if (permissionId !== 'geolocation') {
			return;
		}

		setActivePermissionAction(permissionId);
		dispatch({ type: UNSET_LOCATION });

		window.alert(
			'Revoke location access in your browser or site settings, then return here to refresh the status. The app has also cleared its stored location data.',
		);

		await loadPermissionStatuses();
		setActivePermissionAction(null);
	};

	// run to toggle network.status redux variable
	const network = useAppSelector(
		(state: RootState) => state.network,
		shallowEqual,
	);

	const handleNetworkToggle = () => {
		dispatch({
			type: TOGGLE_NETWORK,
			payload: {
				status: !network.status,
			},
		});
	};

	const handleDevReset = async () => {
		if (!showDevTools || isResettingApp) {
			return;
		}

		const confirmed = window.confirm(
			'Clear cached data and reset the local database?',
		);

		if (!confirmed) {
			return;
		}

		setIsResettingApp(true);

		try {
			const hasWindow = typeof window !== 'undefined';
			const hasNavigator = typeof navigator !== 'undefined';

			await persistor.purge();

			if (hasWindow) {
				window.localStorage?.clear();
				window.sessionStorage?.clear();
			}

			await db.delete();
			await db.open();

			if (hasWindow && 'caches' in window) {
				const cacheNames = await caches.keys();
				await Promise.all(
					cacheNames.map((cacheName) => caches.delete(cacheName)),
				);
			}

			if (hasNavigator && 'serviceWorker' in navigator) {
				const registrations =
					await navigator.serviceWorker.getRegistrations();
				await Promise.all(
					registrations.map((registration) =>
						registration.unregister(),
					),
				);
			}

			window.location.reload();
		} catch (error) {
			console.error('Failed to reset local data', error);
		} finally {
			setIsResettingApp(false);
		}
	};

	return (
		<IonPage>
			<IonContent fullscreen>
				<main>
					<PageHeader title="Explorer profile" />

					<ProfileSection>
						<ProfileAvatar>
							<ProfileAvatarImg
								src={user_prefs.avatar}
								onClick={() => setIsOpen(true)}
							/>
							<IonInput
								value={user_prefs.name}
								placeholder="Explorer"
								maxlength={14}
								onIonInput={(e) => {
									handleChangeName(e);
								}}
							/>
							<p>Tap to change</p>
						</ProfileAvatar>
					</ProfileSection>

					<ProfileSection>
						<ProfileSectionTitle>Accessibility</ProfileSectionTitle>
						<p className="description">
							Just show me trails and places that have easier or
							accessible access
						</p>
						<ChecklistItem>
							<IonLabel>Easy access</IonLabel>
							<IonToggle
								checked={Boolean(user_prefs.accessible)}
								onIonChange={(e) =>
									handleChangeAccessibility(e)
								}></IonToggle>
						</ChecklistItem>
					</ProfileSection>

					<Range />

					<ProfileSection>
						<ProfileSectionTitle>
							Preferred units
						</ProfileSectionTitle>
						<p className="description">
							Set your preferred units...
						</p>

						<IonSegment
							className="mt4"
							value={user_prefs.preferred_dist_units}
							onIonChange={(e) => {
								handlePreferredUnits(e);
							}}>
							<IonSegmentButton value="miles">
								<IonLabel>Miles</IonLabel>
							</IonSegmentButton>
							<IonSegmentButton value="km">
								<IonLabel>Kilometers</IonLabel>
							</IonSegmentButton>
						</IonSegment>

						<IonSegment
							className="mt4"
							value={user_prefs.preferred_temp_units}
							onIonChange={(e) => {
								handlePreferredTempUnits(e);
							}}>
							<IonSegmentButton value="c">
								<IonLabel>Celsius (°C)</IonLabel>
							</IonSegmentButton>
							<IonSegmentButton value="f">
								<IonLabel>Farenheit (°F)</IonLabel>
							</IonSegmentButton>
						</IonSegment>
					</ProfileSection>

					<ActivitiesList activities={activities} />

					<TopicsList topics={topics} />

					{showDevTools && (
						<ProfileSection>
							<ProfileSectionTitle>
								Developer tools
							</ProfileSectionTitle>
							<p className="description">
								Clears cached assets, persisted state and the
								local Dexie database for a clean restart.
							</p>
							<IonButton
								expand="block"
								color="danger"
								onClick={handleDevReset}
								disabled={isResettingApp}>
								{isResettingApp
									? 'Resetting...'
									: 'Reset local cache'}
							</IonButton>
						</ProfileSection>
					)}

					<ProfileSection>
						<ProfileSectionTitle>
							App permissions
						</ProfileSectionTitle>
						<p className="description">
							Current status of permissions requested by the app.
						</p>
						<PermissionList>
							{permissionStatuses.map((permission) => (
								<PermissionCard key={permission.id}>
									<PermissionInfo>
										<h3>{permission.label}</h3>
										<p>{permission.detail}</p>
									</PermissionInfo>
									<PermissionActions>
										<PermissionStatusBadge>
											{permission.status}
										</PermissionStatusBadge>
										<IonButton
											size="small"
											fill="outline"
											disabled={
												activePermissionAction ===
												permission.id
											}
											onClick={() =>
												permission.status === 'Allowed'
													? handleRevokePermission(
															permission.id,
													  )
													: handleRequestPermission(
															permission.id,
													  )
											}>
											{activePermissionAction ===
											permission.id
												? permission.status ===
												  'Allowed'
													? 'Working...'
													: 'Requesting...'
												: permission.status ===
												  'Allowed'
												? 'Revoke'
												: 'Request'}
										</IonButton>
									</PermissionActions>
								</PermissionCard>
							))}
						</PermissionList>
					</ProfileSection>

					{/* Dev Options */}

					{/*<ProfileSection>*/}
					{/*	<ProfileSectionTitle>Dev Options</ProfileSectionTitle>*/}
					{/*	<ChecklistItem>*/}
					{/*		<IonLabel>Network Status</IonLabel>*/}
					{/*		<IonToggle*/}
					{/*			className=""*/}
					{/*			checked={network.status}*/}
					{/*			onClick={() =>*/}
					{/*				handleNetworkToggle()*/}
					{/*			}></IonToggle>*/}
					{/*	</ChecklistItem>*/}
					{/*</ProfileSection>*/}
				</main>
				<Footer />

				<IonModal
					ref={modal}
					isOpen={isOpen}
					presentingElement={presentingElement}>
					<IonHeader>
						<IonToolbar>
							<CloseButton
							onClick={() => setIsOpen(false)}
							icon={close}
						/>
							<IonTitle>Select your avatar image&hellip;</IonTitle>
						</IonToolbar>
					</IonHeader>
					<IonContent>
						
						<ProfileAvatarStack>
							{avatarOptions.map((avatar) => {
								return (
									<ProfileAvatarImg
										key={avatar}
										src={avatar}
										onClick={(e) => {
											handleChangeAvatar(e);
											setIsOpen(false);
										}}
									/>
								);
							})}
						</ProfileAvatarStack>
					</IonContent>
				</IonModal>
			</IonContent>
		</IonPage>
	);
};

export default Profile;
